import {Observable} from 'rxjs'
import {TypedFormGroup} from '../../../../../core/forms/form-controls.model'
import {
  BoilerDecalingStaticJobQuestionsInput,
  CatDoorInstallationStaticJobQuestionsInput,
  HedgeTrimmingStaticJobQuestionsInput,
  InstallLampsStaticJobQuestionsInput,
  LawnMaintenanceStaticJobQuestionsInput,
  LawnMowningStaticJobQuestionsInput,
  LayingParquetStaticJobQuestionsInput,
  ParquetMaintenanceStaticJobQuestionsInput,
  PriceRange,
  RenewableEnergyConsultingQuestionsInput,
  SolarInstallationStaticJobQuestionsInput,
  StaticJobQuestionsInput,
  TreeCuttingStaticJobQuestionsInput,
} from '../../../../../../__generated__/types'

// Mappings of the offering that have a static form implemented (replacing the dynamic form defined in the backend)
export enum StaticOfferings {
  LampInstallation = '83',
  CatDoorInstallation = '109',
  RenewableEnergyConsulting = '123',
  LawnMaintenance = '216',
  LawnMowing = '237',
  LawnScarifying = '214',
  TreeCutting = '238',
  HedgeTrimming = '239',
  BoilerDescaling = '14',
  LayingParquet = '150',
  ParquetMaintenance = '151',
  SolarInstallation = '119',
}

export type StaticFormQuestions =
  | InstallLampsStaticJobQuestionsInput
  | CatDoorInstallationStaticJobQuestionsInput
  | RenewableEnergyConsultingQuestionsInput
  | LawnMaintenanceStaticJobQuestionsInput
  | LawnMowningStaticJobQuestionsInput
  | TreeCuttingStaticJobQuestionsInput
  | HedgeTrimmingStaticJobQuestionsInput
  | BoilerDecalingStaticJobQuestionsInput
  | LayingParquetStaticJobQuestionsInput
  | ParquetMaintenanceStaticJobQuestionsInput
  | SolarInstallationStaticJobQuestionsInput

export type StaticFormGroup<T = StaticFormQuestions> = TypedFormGroup<T>

export interface StaticComponent<T = StaticFormQuestions> {
  new (...any): StaticComponentInstance<T>
  createForm(): StaticFormGroup<T>
}

export interface StaticComponentInstance<Questions = StaticFormQuestions, ExtraInfo = unknown> {
  /**
   * Input property that will get filled with the form group that was created by the static form component.
   * This form property must be used for wiring the form state, as it's also used upstream
   */
  form: StaticFormGroup<Questions>

  /**
   * Method that must be implemented to return the questions input for the current form state.
   */
  questionsInput(): StaticJobQuestionsInput

  /**
   * Only implement this method if you need to get the price range calculations for the current form state.
   * The argument provided is an observable that will emit the price range every time it's calculated.
   */
  setPriceRange?(priceRange$: Observable<PriceRange>): void

  /**
   * Only implement this method if you need to override the default layout configuration.
   */
  layout$?(): Observable<StaticFormLayoutConfig>

  /**
   * Implement saveExtraInfo when you need to persist extra information in the session storage
   * so that the user can navigate away from the page and come back to the same state.
   * @return the extra information that will be persisted for you.
   */
  saveExtraInfo?(): ExtraInfo

  /**
   * Implement hydrateExtraInfo when you need to rehydrate the extra information from the session storage
   * that you saved with `saveExtraInfo`
   * @param additionalInfo information that was saved with `saveExtraInfo`
   */
  hydrateExtraInfo?(additionalInfo: ExtraInfo): void
}

export interface Offering {
  id: string
  label: string
  icon?: {name: string; collection: string}
}

export interface StaticFormLayoutConfig {
  showBreakdown?: boolean
  infoText?: string
  numberOfItems?: number
}
